import { Component } from "@theme/component";

/**
 * @typedef {object} PersonalisationInputRefs
 * @property {HTMLInputElement} input
 * @property {HTMLElement} [errorMessage]
 */

/**
 * Wraps the personalisation text field and its own inline validation
 * message. Fully self-contained — it doesn't know whether a canvas
 * exists anywhere on the page. On every keystroke it announces its value
 * via a `personalisation:input` CustomEvent; any
 * personalisation-canvas-component listening (assets/personalisation-canvas.js)
 * picks it up. That's the only link between the two.
 *
 * Enforces the field being valid (non-empty, and only allowed characters)
 * through four layers:
 *  1. Native browser validation, via `required` + `pattern` + an explicit
 *     `form` attribute association (see connectedCallback).
 *  2. `invalid` suppresses the browser's own validation bubble. It doesn't
 *     drive the visible message — that event can fire from anything
 *     calling reportValidity() on the form, not just a genuine submit
 *     attempt, so it isn't reliable evidence of one.
 *  3. The visible message is shown from a click on a submit button, Enter
 *     in this field, or the form's `submit` event — see #validate.
 *  4. `.personalisation-incomplete` toggled on `<html>`, which
 *     personalisation.css uses to disable Shopify's accelerated-checkout
 *     buttons (Buy it now, Shop Pay) while the field is invalid — those
 *     bypass (1)–(3) entirely, see #updateIncompleteState.
 *
 * The visible message only appears after a submit attempt — never just
 * from page load or from typing on its own — see hasAttemptedSubmit.
 *
 * @extends Component<PersonalisationInputRefs>
 */
class PersonalisationInput extends Component {
  requiredRefs = ["input"];
  /** Whether a submit attempt has happened yet — gates the visible error message. */
  hasAttemptedSubmit = false;

  /** Letters, digits, space, apostrophe, hyphen. Adjust if the print process allows more. */
  static allowedCharsPattern = "[A-Za-z0-9 '-]*";
  static disallowedCharsRegExp = /[^A-Za-z0-9 '-]/g;

  connectedCallback() {
    super.connectedCallback();

    this.refs.input.setAttribute("pattern", PersonalisationInput.allowedCharsPattern);

    this.form = this.closest("form") || document.querySelector('form[data-type="add-to-cart-form"]');

    // This input renders inside _product-details, a sibling of the <form>
    // buy-buttons.liquid renders — not a descendant of it. Native
    // `required`/`pattern` validation only applies to controls that are
    // DOM descendants of a form, or explicitly associated via the `form`
    // attribute. Use `getAttribute("id")`, not `.id` — the product form
    // has a hidden `<input name="id">` (the variant id), and named form
    // controls shadow a <form>'s own built-in properties, so `.id` would
    // return that input element instead of the form's ID string.
    const formId = this.form?.getAttribute("id");
    if (formId) {
      this.refs.input.setAttribute("form", formId);
    } else {
      console.warn("[personalisation-input] Could not find the product form's id — native validation won't apply.");
    }

    // Suppresses the browser's default validation bubble. Doesn't show our
    // own message here — `invalid` can fire from anything calling
    // reportValidity() on the form, not just a genuine submit attempt
    // (Shopify's own accelerated-checkout button appears to probe form
    // validity this way on mount). The message is shown separately below,
    // only for actions that are unambiguously a submit attempt.
    this.handleInvalid = (event) => event.preventDefault();
    this.refs.input.addEventListener("invalid", this.handleInvalid);

    // A click on a submit button, or Enter in this field, are genuine
    // submit attempts.
    this.handleSubmitButtonClick = () => this.#validate();
    this.form?.querySelectorAll('[type="submit"]').forEach((button) => {
      button.addEventListener("click", this.handleSubmitButtonClick);
    });
    this.handleEnterKey = (event) => {
      if (event.key === "Enter") this.#validate();
    };
    this.refs.input.addEventListener("keydown", this.handleEnterKey);

    // Backstop for cases where the `form` association above didn't apply.
    this.handleFormSubmit = (event) => this.#validate(event);
    this.form?.addEventListener("submit", this.handleFormSubmit);

    // Sets the initial disabled state for accelerated-checkout buttons
    // without showing the visible message yet.
    this.#updateIncompleteState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.form?.removeEventListener("submit", this.handleFormSubmit);
    this.refs.input.removeEventListener("invalid", this.handleInvalid);
    this.refs.input.removeEventListener("keydown", this.handleEnterKey);
    this.form?.querySelectorAll('[type="submit"]').forEach((button) => {
      button.removeEventListener("click", this.handleSubmitButtonClick);
    });
  }

  handleInput() {
    this.#sanitizeValue();
    this.#updateIncompleteState();

    if (this.hasAttemptedSubmit) {
      this.#updateErrorMessage();
    }

    this.dispatchEvent(
      new CustomEvent("personalisation:input", {
        bubbles: true,
        composed: true,
        detail: {
          textInputId: this.refs.input.id,
          value: this.refs.input.value,
        },
      })
    );
  }

  /** Strips disallowed characters as they're typed, preserving cursor position. */
  #sanitizeValue() {
    const { input } = this.refs;
    const sanitized = input.value.replace(PersonalisationInput.disallowedCharsRegExp, "");
    if (sanitized === input.value) return;

    const cursorFromEnd = input.value.length - (input.selectionEnd ?? input.value.length);
    input.value = sanitized;
    const newPosition = Math.max(0, sanitized.length - cursorFromEnd);
    input.setSelectionRange(newPosition, newPosition);
  }

  /**
   * @param {SubmitEvent} [event] Passed only when called from the form's
   *   `submit` listener — absent when called from handleInput().
   */
  #validate(event) {
    const isInvalid = !this.refs.input.checkValidity();

    if (isInvalid && event) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    this.hasAttemptedSubmit = true;
    this.#updateIncompleteState();
    this.#updateErrorMessage();
  }

  /**
   * Silent — never shows or hides the visible message, only keeps
   * `.personalisation-incomplete` on `<html>` in sync. personalisation.css
   * uses that class to disable Shopify's accelerated-checkout buttons
   * (Buy it now, Shop Pay), which read the form's data directly on click
   * and never dispatch a `submit` event, so nothing else here can reach
   * them. Runs from page load, not just on submit attempts, since those
   * buttons can be clicked before the field is ever touched.
   */
  #updateIncompleteState() {
    document.documentElement.classList.toggle("personalisation-incomplete", !this.refs.input.checkValidity());
  }

  /** Shows/hides the visible inline message. Only ever called after a submit attempt. */
  #updateErrorMessage() {
    const { input, errorMessage } = this.refs;
    if (!errorMessage) return;

    if (input.validity.valid) {
      errorMessage.classList.remove("addVisibility");
      return;
    }

    const message = input.validity.patternMismatch
      ? errorMessage.dataset.patternMessage || "Please use only letters, numbers, spaces, apostrophes, and hyphens"
      : errorMessage.dataset.requiredMessage || "This field is required";

    errorMessage.textContent = message;
    errorMessage.classList.add("addVisibility");
  }
}

customElements.define("personalisation-input-component", PersonalisationInput);
